import getResponseArray from '@tymbe/utils/getResponseArray';
import { Form, FormApi, FormState } from 'informed';
import moment from 'moment/moment';
import { JSX, useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useIsFetching, useQuery, useQueryClient } from 'react-query';

import CalendarItem from './components/CalendarItem';
import TyCalWeek from './components/CalWeek/TyCalWeek';
import CompanyBranchSelector from './components/CompanyBranchSelector';
import DaySummary from './components/DaySummary';
import CrossIcon from './components/icons/CrossIcon';
import ShiftCreate from './components/ShiftCreate/ShiftCreate';
import TemplateFilter from './components/TemplateFilter';
import apiClient from '../../apiClient';
import Spinner from '../../components/Spinner';
import useSearchString from '../../hooks/UrlParamsHandler/useSearchParams';
import { queryKeyContains } from '../../utils/reactQueryUtils';
import ShiftFilter, { ShiftFilterOptions } from './components/ShiftFilter';
import DateHandlingBar from './components/DateHandlingBar';
import WeekStatsHeader from './components/WeekStatsHeader';
import TyCalDayOfWeekWithBadge from './components/CalWeek/TyCalDayOfWeekWithBadge';
import { ApplicationState } from '../../utils/enums';
import ShiftDetailModal from './components/ShiftDetailModal';
import ShiftAttendancePage from './shiftAttendance.page';
import TyCalWeekNoData from './components/CalWeek/TyCalWeekNoData';
import TyCalWeekLoading from './components/CalWeek/TyCalWeekLoading';
import { DropDownMenuOption } from './components/ShiftDetail/DropDownMenu';
import { FavouriteIcon, NavigationIcon } from '@tymbe/ui-icons';
import { ShiftDetailHistory } from './components/ShiftDetailHistory/ShiftDetailHistory';
import Options from './components/Options';

type UseCalendarShiftsFilters = {
  templates?: number[];
  companyId?: number;
  branchofficeId?: number;
  departmentId?: number;
  shiftFilter?: string;
};

const useCalendarShifts = (
  start: string,
  filters: UseCalendarShiftsFilters = {}
) =>
  useQuery(
    ['shifts', 'calendar', start, filters],
    async () => {
      const modifiers: Record<string, boolean> = {
        withApplicationCounts: true,
      };
      if (filters.shiftFilter === ShiftFilterOptions.NOT_FULL)
        modifiers.unFilledOnly = true;
      if (filters.shiftFilter === ShiftFilterOptions.UNCONFIRMED_ATTENDANCE)
        modifiers.unconfirmedAttendance = true;

      const query = {
        start_time: {
          $gte: moment(start).startOf('week').toISOString(),
          $lte: moment(start).endOf('week').toISOString(),
        },
        'shift.company_id': filters.companyId,
        parent_id: filters.branchofficeId,
        branchoffice_id: filters.departmentId,
        $limit: -1,
        $modify: modifiers,
        $eager: '[application.attendance, perk, shiftTemplate]',
        $joinRelation: 'branchoffice',
        shift_template_id: {},
      };
      if (filters.templates?.length) {
        query.shift_template_id = { $in: filters.templates };
      }
      return apiClient.service('shift').find({ query });
    },
    { staleTime: 1000 * 5, keepPreviousData: true }
  );

const CalendarPage = () => {
  const [showHistory, setShowHistory] = useState(false);
  const ref = useRef<FormApi<object>>(undefined);
  const queryClient = useQueryClient();
  const now = moment().startOf('week').toISOString();
  const [searchParams, setSearchParams] = useSearchString();
  const start = searchParams.get('start') ?? now;
  const shiftId = searchParams.get('shiftAttendance') || undefined;
  const companyId = Number(searchParams.get('company_id')) || undefined;
  const branchofficeId =
    Number(searchParams.get('branchoffice_id')) || undefined;
  const departmentId = Number(searchParams.get('department_id')) || undefined;
  const shiftFilter = searchParams.get('shiftFilter') || undefined;
  const [initialValues] = useState({
    company: companyId,
    branchoffice: branchofficeId,
    department: departmentId,
  });
  const [templates, setTemplates] = useState<number[]>([]);

  const [createModal, setCreateModal] = useState(false);
  const calendarIsFetching = useIsFetching({
    predicate: queryKeyContains('calendar'),
  });
  const shiftAttendanceId = searchParams.get('shiftAttendance') || undefined;

  const shiftReq = useCalendarShifts(start, {
    templates,
    companyId,
    branchofficeId,
    departmentId,
    shiftFilter,
  });
  const shifts = getResponseArray(shiftReq.data)
  const { data: company } = useQuery(
    ['company', companyId],
    () => apiClient.service('company').get(companyId!),
    { enabled: !!companyId, staleTime: 1000 * 60 * 5 }
  );
  const { data: department } = useQuery(
    ['branchoffice', departmentId],
    () => apiClient.service('branchoffice').get(departmentId!),
    { enabled: !!departmentId, staleTime: 1000 * 60 * 5 }
  );

  const onShiftAttendanceClose = useCallback(() => {
    setShowHistory(false);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('shiftAttendance');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const onCompanyBranchChange = useCallback(
    ({ values }: FormState<any>) => {
      const newParams = new URLSearchParams(searchParams);

      if (values.company) {
        newParams.set('company_id', values.company);
      } else {
        newParams.delete('company_id');
      }
      if (values.branchoffice) {
        newParams.set('branchoffice_id', values.branchoffice);
      } else {
        newParams.delete('branchoffice_id');
      }
      if (values.department) {
        newParams.set('department_id', values.department);
      } else {
        newParams.delete('department_id');
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const calendarContentBuilder = (): JSX.Element | JSX.Element[] => {
    if (shiftReq.isLoading) {
      return <TyCalWeekLoading />;
    }

    if (
      !shiftReq.isLoading &&
      shiftReq.isFetched &&
      shiftReq.data?.length === 0
    ) {
      return <TyCalWeekNoData />;
    }

    return parseShiftsToTyCalWeekEvents();
  };

  const parseShiftsToTyCalWeekEvents = (): JSX.Element[] => {
    return shifts.map((shift) => (
      <TyCalWeek.Event
        date={shift.start_time}
        data={shift}
        key={shift.id}
        className="relative"
      >
        <CalendarItem
          data-date={shift.start_time}
          currentApplications={shift.filled_orders_count || 0}
          totalApplications={shift.orders_count || 0}
          hasUnconfirmedAttendance={
            !!shift.application?.some(({ attendance }) => attendance === null)
          }
          pendingInvitationsCount={
            shift.application?.filter(
              (app) =>
                app.invitation === true &&
                app.state !== ApplicationState.CONFIRMED
            ).length || 0
          }
          emoji={shift.emoji || shift.shiftTemplate?.emoji}
          start={shift.start_time}
          end={shift.end_time}
          className="cursor-pointer"
          shiftId={shift.id}
        />
      </TyCalWeek.Event>
    ));
  };

  const toggleShowHistory = () => {
    setShowHistory((prev) => !prev);
  };

  const menuOptions: DropDownMenuOption[] = [
    {
      text: 'Historie',
      icon: (
        <FavouriteIcon
          name="watch_later"
          className="text-secondary-400 size-4"
        />
      ),
      onClick: () => {
        toggleShowHistory();
      },
    },
  ];

  useEffect(() => {
    const newValues = {
      company: companyId,
      branchoffice: branchofficeId,
      department: departmentId,
    };
    ref.current?.setValues(newValues);
  }, [branchofficeId, companyId, departmentId]);

  return (
    <>
      <div className="flex gap-6 w-full items-center justify-between px-10 py-6">
        <div className="flex gap-6 items-center justify-start">
          <h3 className="text-heading-3-xl text-secondary-600">Kalendář</h3>
          <Form
            onChange={onCompanyBranchChange}
            formApiRef={ref}
            className="flex gap-2 py-2"
            initialValues={initialValues}
          >
            <CompanyBranchSelector />
          </Form>
        </div>
        <button
          className="py-2 px-4 text-body-2-xl bg-primary-200 text-primary-800 disabled:bg-secondary-100 disabled:text-secondary-400"
          type="button"
          disabled={!company || !department}
          onClick={() => setCreateModal(!createModal)}
          title={
            !company || !department ? 'Vyberte firmu a oddělení' : undefined
          }
        >
          Vypsat směny
        </button>
      </div>
      <TemplateFilter
        start={start}
        companyId={companyId}
        branchofficeId={departmentId}
        onChange={setTemplates}
      />
      <div className="flex w-full h-full">
        <div className="h-full flex flex-col flex-1">
          <div className="flex gap-3 p-3 bg-white items-center border-b border-secondary-100">
            <DateHandlingBar />
            <ShiftFilter />
            <Spinner className="size-4" show={!!calendarIsFetching} />
          </div>
          <div className="h-full w-full relative">
            <TyCalWeek
              headComp={TyCalDayOfWeekWithBadge}
              date={start}
              groupPosition="bottom"
              className="absolute top-0 left-0 w-full h-full rounded grow [&_[data-day='6']]:bg-secondary-50 [&_[data-day='7']]:bg-secondary-50"
            >
              <TyCalWeek.Group
                flat
                position="bottom"
                className="bg-secondary-50"
              >
                <TyCalWeek.Group.Head className="bg-secondary-50 flex !justify-center">
                  <WeekStatsHeader />
                </TyCalWeek.Group.Head>
                <TyCalWeek.Header headComp={DaySummary} />
              </TyCalWeek.Group>
              {calendarContentBuilder()}
              <ShiftDetailModal />
            </TyCalWeek>
          </div>
        </div>
      </div>

      {!company || !department ? null : (
        <Modal
          isOpen={createModal}
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEsc={false}
          ariaHideApp={false}
          className="fixed top-6 right-6 bottom-6 left-6 bg-bg rounded-lg overflow-auto drop-shadow-lg z-50 flex flex-col"
          overlayClassName="bg-overlay fixed inset-0 z-modal"
          onRequestClose={() => setCreateModal(false)}
        >
          <CrossIcon
            onClick={() => setCreateModal(false)}
            className="absolute right-6 top-6 cursor-pointer"
          />
          <ShiftCreate
            departmentId={departmentId!}
            onPublish={() => {
              queryClient.invalidateQueries({
                predicate: queryKeyContains('calendar'),
              });
              setCreateModal(false);
            }}
            onCancel={() => {
              setCreateModal(false);
            }}
          />
        </Modal>
      )}
      <Modal
        isOpen={!!shiftAttendanceId}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        ariaHideApp={false}
        className="fixed top-4 right-4 bottom-4 left-4 bg-bg rounded-lg drop-shadow-lg z-50 flex flex-col"
        overlayClassName="bg-overlay fixed inset-0 z-modal"
        onRequestClose={onShiftAttendanceClose}
      >
        {/* @HOTFIX Refactor and sync with ShiftDetailHeader */}
        <div className="text-secondary-600 text-heading-4-xl p-6 flex align-center border-b border-secondary-100 relative">
          {showHistory ? (
            <>
              <NavigationIcon
                name="west"
                onClick={toggleShowHistory}
                className="mr-2 size-5 cursor-pointer"
              />
              Historie
            </>
          ) : (
            <>
              <div>Detail směny</div>

              <Options
                menuOptions={menuOptions}
                dropDownMenuClassName="left-[255px]"
                className="text-secondary-400"
              />
            </>
          )}

          <CrossIcon
            onClick={onShiftAttendanceClose}
            className="cursor-pointer ml-auto"
          />
        </div>
        {showHistory && shiftId ? (
          <div className='p-5 overflow-auto'>
            <ShiftDetailHistory shiftId={shiftId} />
          </div>
        ) : (
          <ShiftAttendancePage onSuccessCallback={shiftReq.refetch} />
        )}
      </Modal>
    </>
  );
};

export default CalendarPage;
